import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TeamsAverageGoals } from './TeamsAverageGoals';
import { MainMatchesData, getMatchAnalysis } from './MainMatchesData';
import { LastMatchesAndResultPrevision } from './LastMatchesAndResultPrevision';
import { GoalsPrediction } from './GoalsPrediction';
import { GoalPerMinuteAndHead2Head } from './GoalPerMinuteAndHead2Head';
import { AIMatchPrediction } from './AIMatchPrediction';

import {
  FaChevronLeft,
  FaChevronRight,
  FaChartBar,
  FaFutbol,
  FaCalendarAlt,
  FaRobot,
} from 'react-icons/fa';
import { Footer } from './Footer';
import { GiSoccerField } from 'react-icons/gi';

export const MatchAnalysisPage = () => {
  const { t } = useTranslation();
  const { matchId } = useParams();
  const [matchAnalysis, setMatchAnalysis] = useState<MainMatchesData>();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [activeColumn, setActiveColumn] = useState<number>(1); // 0=left, 1=center, 2=right
  const [loading, setLoading] = useState<boolean>(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const parsedMatchId = matchId ? parseInt(matchId, 10) : undefined;

  useEffect(() => {
    doGetMatchAnalysis();

    // Add resize listener for responsive behavior
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const doGetMatchAnalysis = async () => {
    setLoading(true);
    if (parsedMatchId !== undefined) {
      try {
        const retMatches = await getMatchAnalysis(parsedMatchId);
        setMatchAnalysis(retMatches);

        // Only scroll on desktop
        if (!isMobile) {
          setTimeout(() => {
            if (containerRef.current) {
              const containerWidth = containerRef.current.scrollWidth;
              const viewportWidth = window.innerWidth;
              const scrollPosition = (containerWidth - viewportWidth) / 2;

              window.scrollTo({
                top: 0,
                left: scrollPosition,
                behavior: 'smooth',
              });
            }
          }, 1);
        }
      } catch (error) {
        console.error('Error fetching match analysis:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const goToNextColumn = () => {
    if (activeColumn < 2) {
      setActiveColumn(activeColumn + 1);
    }
  };

  const goToPrevColumn = () => {
    if (activeColumn > 0) {
      setActiveColumn(activeColumn - 1);
    }
  };

  // Get column title based on index
  const getColumnTitle = (index: number): string => {
    switch (index) {
      case 0:
        return matchAnalysis!.Home + ' ' + t('matchAnalysisPage.stats');
      case 1:
        return t('matchAnalysisPage.matchAnalysis');
      case 2:
        return matchAnalysis!.Away + ' ' + t('matchAnalysisPage.stats');
      default:
        return '';
    }
  };

  // Get column icon based on index
  const getColumnIcon = (index: number) => {
    switch (index) {
      case 0:
        return <FaChartBar className="text-teal-400 mr-2" />;
      case 1:
        return <FaFutbol className="text-teal-400 mr-2" />;
      case 2:
        return <FaChartBar className="text-teal-400 mr-2" />;
      default:
        return null;
    }
  };

  // Render column navigation for mobile
  const renderColumnNavigation = () => {
    return (
      <div className="column-navigation bg-gradient-to-r from-slate-900 to-indigo-900 border-t border-slate-700">
        <div className="column-title flex items-center justify-center">
          {getColumnIcon(activeColumn)}
          {getColumnTitle(activeColumn)}
        </div>
        <div className="navigation-buttons">
          <button
            onClick={goToPrevColumn}
            className={`nav-button left ${activeColumn === 0 ? 'opacity-50 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            disabled={activeColumn === 0}
          >
            <FaChevronLeft />
          </button>
          <div className="column-indicators">
            <div
              className={`indicator ${activeColumn === 0 ? 'bg-teal-400 scale-125' : 'bg-slate-600'}`}
            ></div>
            <div
              className={`indicator ${activeColumn === 1 ? 'bg-teal-400 scale-125' : 'bg-slate-600'}`}
            ></div>
            <div
              className={`indicator ${activeColumn === 2 ? 'bg-teal-400 scale-125' : 'bg-slate-600'}`}
            ></div>
          </div>
          <button
            onClick={goToNextColumn}
            className={`nav-button right ${activeColumn === 2 ? 'opacity-50 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            disabled={activeColumn === 2}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    );
  };

  // Get the column to show for each component based on active column
  const getColumnToShow = (
    index: number,
  ): 'left' | 'center' | 'right' | 'all' => {
    if (!isMobile) return 'all';

    switch (index) {
      case 0:
        return 'left';
      case 1:
        return 'center';
      case 2:
        return 'right';
      default:
        return 'all';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-500"></div>
          <p className="loading-text mt-4">
            {t('matchAnalysisPage.loadingMatchAnalysis')}
          </p>
        </div>
      </div>
    );
  }

  if (!matchAnalysis) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-indigo-900">
        <div className="bg-slate-800 bg-opacity-50 p-8 rounded-xl shadow-xl backdrop-blur-sm border border-slate-700 text-center">
          <div className="text-5xl text-red-500 mb-4">!</div>
          <h2 className="text-xl font-bold text-white mb-4">
            {t('matchAnalysisPage.matchNotFound')}
          </h2>
          <p className="text-gray-300 mb-6">
            {t('matchAnalysisPage.matchNotFoundDescription')}
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
          >
            {t('matchAnalysisPage.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="match-analysis-wrapper h-full min-h-screen pt-16">
      {isMobile && renderColumnNavigation()}

      <div
        ref={containerRef}
        className={
          isMobile
            ? 'mobile-analysis-container px-4'
            : 'AnalysisContainer max-w-7xl mx-auto sm:px-6 lg:px-8'
        }
      >
        <div
          className={`bg-gradient-to-r from-teal-800 to-indigo-900 p-4 mb-6 rounded-xl shadow-xl border border-gray-700 ${!isMobile ? 'mt-1 min-w-[1145px] w-[1145px] max-w-full' : 'mt-11'}`}
        >
          <h1 className="text-xl sm:text-2xl font-bold text-white text-center">
            <span className="text-teal-400">{matchAnalysis.Home}</span>
            <span className="mx-3 text-gray-300">
              {t('matchAnalysisPage.vs')}
            </span>
            <span className="text-teal-400">{matchAnalysis.Away}</span>
          </h1>
          {/* Modified the date container below */}
          <div className="flex justify-center mt-1">
            <div className="flex items-center gap-2 bg-black/20 px-3 py-1 rounded-lg">
              <FaCalendarAlt className="text-teal-400" size={14} />
              <span className="text-sm text-gray-200">
                {matchAnalysis.MatchDate}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* For mobile view, only render the active column */}
          {isMobile && (
            <div className="mobile-content fade-in">
              {/* Each section enclosed in a card */}
              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <GiSoccerField className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.matchOverview')}
                </h2>
                <LastMatchesAndResultPrevision
                  selectedItemData={matchAnalysis}
                  columnToShow={getColumnToShow(activeColumn)}
                />
              </div>

              {matchAnalysis?.AI_Analysis && (
                <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 slide-in-up">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                    <FaRobot className="text-teal-400 mr-2" />
                    {t('matchAnalysisPage.aiMatchPrediction')}
                  </h2>
                  <AIMatchPrediction
                    selectedItemData={matchAnalysis}
                    columnToShow={getColumnToShow(activeColumn)}
                  />
                </div>
              )}

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.teamAverageGoals')}
                </h2>
                <TeamsAverageGoals
                  selectedItemData={matchAnalysis}
                  columnToShow={getColumnToShow(activeColumn)}
                />
              </div>

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.goalPredictions')}
                </h2>
                <GoalsPrediction
                  selectedItemData={matchAnalysis}
                  columnToShow={getColumnToShow(activeColumn)}
                />
              </div>

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.matchStatistics')}
                </h2>
                <GoalPerMinuteAndHead2Head
                  selectedItemData={matchAnalysis}
                  columnToShow={getColumnToShow(activeColumn)}
                />
              </div>
            </div>
          )}

          {/* For desktop view, render all columns */}
          {!isMobile && (
            <div className="desktop-content fade-in">
              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 min-w-[1145px] w-[1145px] max-w-full">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <GiSoccerField className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.matchOverview')}
                </h2>
                <LastMatchesAndResultPrevision
                  selectedItemData={matchAnalysis}
                />
              </div>

              {matchAnalysis?.AI_Analysis && (
                <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 min-w-[1145px] w-[1145px] max-w-full slide-in-up">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                    <FaRobot className="text-teal-400 mr-2" />
                    {t('matchAnalysisPage.aiMatchPrediction')}
                  </h2>
                  <AIMatchPrediction selectedItemData={matchAnalysis} />
                </div>
              )}

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 min-w-[1145px] w-[1145px] max-w-full slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.teamAverageGoals')}
                </h2>
                <TeamsAverageGoals selectedItemData={matchAnalysis} />
              </div>

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 min-w-[1145px] w-[1145px] max-w-full slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.goalPredictions')}
                </h2>
                <GoalsPrediction selectedItemData={matchAnalysis} />
              </div>

              <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700 p-4 mb-6 min-w-[1145px] w-[1145px] max-w-full slide-in-up">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaChartBar className="text-teal-400 mr-2" />
                  {t('matchAnalysisPage.matchStatistics')}
                </h2>
                <GoalPerMinuteAndHead2Head selectedItemData={matchAnalysis} />
              </div>
            </div>
          )}
        </div>
        {isMobile && <Footer />}
        {!isMobile && (
          <div className="min-w-[1145px] w-[1145px] max-w-full">
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};
